import { AdminPageHeader } from '@/components/patterns/admin-page'
import SaveBar from '@/components/forms/SaveBar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { RichTextContent } from '@/components/ui/rich-text-content'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { useIsReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { useAppForm } from '@/lib/form'
import {
  flowerSubscriptionSettingsQueries,
  flowerSubscriptionSettingsQueryKeys,
  useFlowerSubscriptionSettings,
  useUpdateFlowerSubscriptionSettingsMutation,
} from '@/lib/services/flower-subscription-settings/flower-subscription-settings.query'
import {
  flowerSubscriptionPlatformSettingsFormSchema,
  flowerSubscriptionSettingsFormSchema,
} from '@/lib/services/flower-subscription-settings/flower-subscription-settings.schema'
import queryClient from '@/lib/queryClient'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound, useParams } from '@tanstack/react-router'
import { RotateCcw, ScrollText } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/settings/subscriptions/')({
  component: SubscriptionSettingsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'subscription-settings')) throw notFound()

    await context.queryClient.ensureQueryData(
      flowerSubscriptionSettingsQueries.detail({
        organizationId: params.orgId,
      })
    )

    return {
      isReadOnly: !context.ability.can('edit', 'subscription-settings'),
    }
  },
})

function settingsPayload(response) {
  return response?.data ?? response ?? {}
}

function formDefaults(settings) {
  return {
    expected_platform_version: settings?.platform_terms?.version_number ?? 1,
    platform_html: settings?.platform_terms?.html ?? '',
    platform_text: settings?.platform_terms?.text ?? '',
    expected_policy_version: settings?.policy?.version_number ?? 1,
    policy_html: settings?.policy?.html ?? '',
    policy_text: settings?.policy?.text ?? '',
  }
}

function subscriptionTermsPreviewHtml(platformHtml, policyHtml) {
  return ['<p><strong>Billing and renewals</strong></p>', platformHtml, policyHtml]
    .filter(Boolean)
    .join('')
}

function CheckoutTermsPreview({ platformHtml, policyHtml }) {
  return (
    <div className="mx-auto w-full max-w-xl overflow-hidden rounded-lg border border-border bg-background text-left">
      <ScrollArea
        role="region"
        aria-label="Subscription terms preview"
        type="always"
        className="max-h-64 overflow-hidden sm:max-h-72"
        viewportClassName="max-h-64 overscroll-y-contain sm:max-h-72"
      >
        <div className="px-4 py-3">
          <RichTextContent html={subscriptionTermsPreviewHtml(platformHtml, policyHtml)} />
        </div>
      </ScrollArea>
      <div className="flex items-center gap-3 border-t border-border px-4 py-3">
        <Checkbox aria-hidden="true" tabIndex={-1} className="pointer-events-none" />
        <span className="block text-sm font-medium text-foreground">
          I agree to the subscription terms.
        </span>
      </div>
    </div>
  )
}

export function SubscriptionSettingsPage() {
  const { orgId } = useParams({ strict: false })

  return <SubscriptionSettingsPageContent key={orgId} organizationId={orgId} />
}

function SubscriptionSettingsPageContent({ organizationId }) {
  const isGlobalOrg = useIsGlobalOrg()
  const { isReadOnly } = useIsReadOnly()
  const { data: settingsResponse } = useFlowerSubscriptionSettings({
    organizationId,
  })
  const settings = settingsPayload(settingsResponse)
  const editableTerms = isGlobalOrg ? settings.platform_terms : settings.policy
  const defaultTerms = isGlobalOrg ? settings.default_platform_terms : settings.default_policy
  const isProvisionedDefault =
    editableTerms?.is_default === true && editableTerms?.version_number === 1
  const updateMutation = useUpdateFlowerSubscriptionSettingsMutation()
  const form = useAppForm({
    defaultValues: formDefaults(settings),
    validators: {
      onSubmit: isGlobalOrg
        ? flowerSubscriptionPlatformSettingsFormSchema
        : flowerSubscriptionSettingsFormSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return

        try {
          const response = await updateMutation.mutateAsync(
            isGlobalOrg
              ? {
                  platformHtml: value.platform_html,
                  expectedPlatformVersion: value.expected_platform_version,
                }
              : {
                  policyHtml: value.policy_html,
                  expectedPolicyVersion: value.expected_policy_version,
                }
          )
          const savedSettings = settingsPayload(response)
          const savedVersion = isGlobalOrg
            ? savedSettings.platform_terms?.version_number
            : savedSettings.policy?.version_number

          formApi.reset(formDefaults(savedSettings))
          toast.success(
            savedVersion === editableTerms?.version_number
              ? `${isGlobalOrg ? 'Standard billing terms' : 'Subscription policies'} are already up to date`
              : `${isGlobalOrg ? 'Standard billing terms' : 'Subscription policies'} updated`
          )
        } catch (error) {
          const conflict = error?.response?.status === 409
          const currentSettings = settingsPayload(error?.response?.data?.current)
          const currentVersion = isGlobalOrg
            ? currentSettings?.platform_terms?.version_number
            : currentSettings?.policy?.version_number

          if (conflict && currentVersion) {
            queryClient.setQueryData(
              flowerSubscriptionSettingsQueryKeys.detail({
                organizationId,
              }),
              currentSettings
            )
            formApi.reset(formDefaults(currentSettings))
            toast.error(
              `${isGlobalOrg ? 'Standard billing terms' : 'Subscription policies'} were updated by someone else`,
              {
                description: 'The newest version is now loaded. Review it before saving again.',
              }
            )
            return
          }

          toast.error(
            `${isGlobalOrg ? 'Standard billing terms' : 'Subscription policies'} could not be updated`,
            {
              description: getErrorMessage(error),
            }
          )

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const restoreDefaultTerms = () => {
    const fieldPrefix = isGlobalOrg ? 'platform' : 'policy'

    form.setFieldValue(`${fieldPrefix}_html`, defaultTerms?.html ?? '')
    form.setFieldValue(`${fieldPrefix}_text`, defaultTerms?.text ?? '')
  }

  const updatedAt = editableTerms?.updated_at ?? settings.updated_at
  const updatedBy = editableTerms?.updated_by ?? settings.updated_by

  return (
    <div className="max-w-4xl space-y-5">
      <AdminPageHeader
        icon={ScrollText}
        title="Subscription Settings"
        description={
          isGlobalOrg
            ? 'Manage the standard billing and renewal terms shown during subscription checkout.'
            : 'Review the standard billing terms and customize your shop’s subscription policies.'
        }
      />

      <form.AppForm>
        <form.Form className="space-y-5">
          <Card>
            <CardHeader className="gap-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <CardTitle>
                    {isGlobalOrg ? 'Standard Billing Terms' : 'Subscription Policies'}
                  </CardTitle>
                  <CardDescription>
                    {isGlobalOrg
                      ? 'Manage the billing and renewal terms included with every new subscription.'
                      : 'Customize the recommended policies customers accept for your subscriptions.'}
                  </CardDescription>
                </div>

                {!isReadOnly ? (
                  <form.Subscribe
                    selector={(state) =>
                      isGlobalOrg ? state.values.platform_html : state.values.policy_html
                    }
                  >
                    {(termsHtml) => (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={restoreDefaultTerms}
                        disabled={termsHtml === defaultTerms?.html}
                      >
                        <RotateCcw className="size-4" />
                        {isGlobalOrg ? 'Restore Standard Terms' : 'Restore Recommended Policies'}
                      </Button>
                    )}
                  </form.Subscribe>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <Typography variant="supporting">
                  {isGlobalOrg ? 'Standard terms' : 'Shop policies'} version{' '}
                  {editableTerms?.version_number}
                </Typography>
                {isProvisionedDefault ? (
                  <Typography variant="supporting">
                    Using the {isGlobalOrg ? 'standard terms' : 'recommended policies'}
                  </Typography>
                ) : updatedAt ? (
                  <Typography variant="supporting">
                    Last updated {dayjs(updatedAt).format('MMM D, YYYY [at] h:mm A')}
                    {updatedBy?.name ? ` by ${updatedBy.name}` : ''}
                  </Typography>
                ) : (
                  <Typography variant="supporting">
                    Using the {isGlobalOrg ? 'standard terms' : 'recommended policies'}
                  </Typography>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {isGlobalOrg ? (
                <section className="space-y-3">
                  <form.AppField name="platform_html">
                    {(field) => (
                      <field.RichTextInput
                        label="Standard terms customers accept"
                        plainTextFieldName="platform_text"
                        placeholder="Enter the standard subscription terms customers must accept..."
                        readOnlyEmptyText="No standard billing terms are configured."
                      />
                    )}
                  </form.AppField>
                </section>
              ) : (
                <section className="space-y-3">
                  <form.AppField name="policy_html">
                    {(field) => (
                      <field.RichTextInput
                        label="Shop policies customers accept"
                        plainTextFieldName="policy_text"
                        placeholder="Enter your shop’s subscription policies..."
                        readOnlyEmptyText="No subscription policies are configured."
                      />
                    )}
                  </form.AppField>
                </section>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Checkout Preview</CardTitle>
              <CardDescription>
                This is how the combined terms will appear before a customer pays.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form.Subscribe
                selector={(state) => [state.values.platform_html, state.values.policy_html]}
              >
                {([platformHtml, policyHtml]) => (
                  <CheckoutTermsPreview platformHtml={platformHtml} policyHtml={policyHtml} />
                )}
              </form.Subscribe>
            </CardContent>
          </Card>

          {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
        </form.Form>
      </form.AppForm>
    </div>
  )
}
